/**
 * Leitwarte: kleine Diagramm-Bausteine ohne Bibliothek.
 *
 * Regeln aus den Mockups: Balken höchstens 24 px dick, oben 4 px gerundet und
 * unten eckig (ein kleines Rechteck am Fuß), Linien 2 px, Marker 8 px mit
 * 2-px-Ring in Kartenfarbe, Gitter als 1-px-Linie #2a2a28, Text nie in der
 * Serienfarbe, keine gestrichelten Linien. Jede Marke trägt einen <title>
 * für den Hover.
 */

import type { CSSProperties, ReactNode } from 'react';
import { zahl } from '@/lib/leitwarte-api';
import { T } from './bausteine';

/** Sequenzielle Blau-Rampe, Stufe 0 = „nichts". */
export const RAMPE = ['#232322', '#0d366b', '#104281', '#184f95', '#1c5cab', '#256abf', '#2a78d6', '#3987e5', '#5598e7', '#6da7ec'] as const;

/** Stufe 0 für 0, sonst 2..top relativ zum Maximum (monoton, so wie die Mockups gestuft sind). */
export function rampStufe(wert: number, max: number, top = 9): number {
  if (!(wert > 0) || !(max > 0)) return 0;
  const anteil = Math.min(1, wert / max);
  return Math.min(top, 1 + Math.max(1, Math.floor(anteil * (top - 1) + 0.25)));
}
export const rampFarbe = (wert: number, max: number, top = 9): string => RAMPE[rampStufe(wert, max, top)];

type Punkt = [number, number];

function linienPunkte(werte: number[], breite: number, hoehe: number, rand: number, vonNull = false): Punkt[] {
  const n = werte.length;
  if (n === 0) return [];
  const max = Math.max(...werte);
  const min = vonNull ? Math.min(0, ...werte) : Math.min(...werte);
  const dx = n > 1 ? (breite - 2 * rand) / (n - 1) : 0;
  return werte.map((v, i) => {
    const t = max > min ? (v - min) / (max - min) : 0.5;
    return [rand + i * dx, rand + (1 - t) * (hoehe - 2 * rand)];
  });
}
const pfad = (pts: Punkt[]) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
const f1 = (n: number) => Number(n.toFixed(1));

// ── Sparkline ────────────────────────────────────────────────────────────────

export function Sparkline({ werte, breite = 170, hoehe = 44, farbe = T.blau, ring = T.karte, punkte = 'beide', label, style }: {
  werte: number[]; breite?: number; hoehe?: number; farbe?: string; ring?: string; punkte?: 'beide' | 'ende' | 'keine'; label?: string; style?: CSSProperties;
}) {
  const pts = linienPunkte(werte, breite, hoehe, 5);
  if (pts.length === 0) return null;
  const erste = pts[0];
  const letzte = pts[pts.length - 1];
  return (
    <svg width={breite} height={hoehe} viewBox={`0 0 ${breite} ${hoehe}`} role="img" aria-label={label} style={{ flexShrink: 0, ...style }}>
      {label ? <title>{label}</title> : null}
      <path d={pfad(pts)} fill="none" stroke={farbe} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {punkte !== 'keine' ? <circle cx={f1(letzte[0])} cy={f1(letzte[1])} r={4} fill={farbe} stroke={ring} strokeWidth={2} /> : null}
      {punkte === 'beide' && pts.length > 1 ? <circle cx={f1(erste[0])} cy={f1(erste[1])} r={4} fill={farbe} stroke={ring} strokeWidth={2} /> : null}
    </svg>
  );
}

// ── Verlauf (Linie mit Fläche) ───────────────────────────────────────────────

export function Verlauf({ werte, breite = 220, hoehe = 50, farbe = T.blau, ring = T.karte, label, style }: {
  werte: number[]; breite?: number; hoehe?: number; farbe?: string; ring?: string; label?: string; style?: CSSProperties;
}) {
  const rand = 5;
  const pts = linienPunkte(werte, breite, hoehe, rand, true);
  if (pts.length === 0) return null;
  const grund = hoehe - rand;
  const letzte = pts[pts.length - 1];
  const flaeche = `${pfad(pts)} L${letzte[0].toFixed(1)},${grund} L${pts[0][0].toFixed(1)},${grund} Z`;
  return (
    <svg width={breite} height={hoehe} viewBox={`0 0 ${breite} ${hoehe}`} role="img" aria-label={label} style={{ flexShrink: 0, ...style }}>
      {label ? <title>{label}</title> : null}
      <path d={flaeche} fill={farbe} fillOpacity={0.16} />
      <path d={pfad(pts)} fill="none" stroke={farbe} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={f1(letzte[0])} cy={f1(letzte[1])} r={4} fill={farbe} stroke={ring} strokeWidth={2} />
    </svg>
  );
}

// ── Säulen (kleine Spaltenbalken, z. B. 24 Stunden) ──────────────────────────

export function Saeulen({ werte, breite = 600, hoehe = 76, farbe = T.blau, luecke = 4, achse, grundlinie = false, titel, label, radius = 3, voll = false }: {
  werte: number[]; breite?: number; hoehe?: number; farbe?: string; luecke?: number;
  /** Achsenbeschriftung unter einzelnen Säulen, z. B. [{ i: 0, text: '0' }]. */
  achse?: Array<{ i: number; text: string }>;
  grundlinie?: boolean; titel?: (i: number, v: number) => string; label?: string; radius?: number;
  /** Auf Kartenbreite skalieren (viewBox) statt fester Pixelgröße. */
  voll?: boolean;
}) {
  const n = werte.length;
  if (n === 0) return null;
  const plotH = hoehe - (achse ? 16 : 0);
  const balkenB = Math.min(24, (breite - 4 - luecke * (n - 1)) / n);
  const links = (breite - (n * balkenB + (n - 1) * luecke)) / 2;
  const max = Math.max(0, ...werte);
  const mitte = (i: number) => links + i * (balkenB + luecke) + balkenB / 2;
  return (
    <svg
      viewBox={`0 0 ${breite} ${hoehe}`}
      width={voll ? undefined : breite}
      height={voll ? undefined : hoehe}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={label}
      style={voll ? { display: 'block', width: '100%', height: 'auto' } : { flexShrink: 0 }}
    >
      {werte.map((v, i) => {
        const h = max > 0 && v > 0 ? Math.max(1, (v / max) * plotH) : 0;
        const x = f1(links + i * (balkenB + luecke));
        const r = Math.min(radius, h / 2);
        const fuss = Math.min(radius, h);
        return (
          <g key={i}>
            <title>{titel ? titel(i, v) : `${i}: ${zahl(v)}`}</title>
            <rect x={x} y={0} width={f1(balkenB)} height={plotH} fill="transparent" />
            {h > 0 ? (
              <>
                <rect x={x} y={f1(plotH - h)} width={f1(balkenB)} height={f1(h)} rx={r} ry={r} fill={farbe} />
                <rect x={x} y={f1(plotH - fuss)} width={f1(balkenB)} height={f1(fuss)} fill={farbe} />
              </>
            ) : null}
          </g>
        );
      })}
      {grundlinie ? <line x1={0} y1={plotH} x2={breite} y2={plotH} stroke={T.rand} strokeWidth={1} /> : null}
      {achse?.map((a) => (
        <text key={a.i} x={f1(mitte(a.i))} y={hoehe - 3} fontSize={10} fill={T.ged} textAnchor="middle">{a.text}</text>
      ))}
    </svg>
  );
}

// ── Gruppierte Balken mit Linie und einer Achse ──────────────────────────────

export interface Gruppe { label: string; werte: number[]; linie?: number; titel?: string }

/** Nächste „schöne" Stufe (1, 2, 2.5, 5 × 10^k) über dem Rohwert. */
function netteStufe(roh: number): number {
  if (!(roh > 0)) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(roh)));
  const rest = roh / mag;
  const f = rest <= 1 ? 1 : rest <= 2 ? 2 : rest <= 2.5 ? 2.5 : rest <= 5 ? 5 : 10;
  return f * mag;
}

export function GruppenBalken({ gruppen, farben = [T.blau, T.orange], linieFarbe = T.aqua, ring = T.karte, breite = 580, hoehe = 290, format = (v: number) => zahl(v), achsenFormat = (v: number) => zahl(v), direktLinie = true, direktBalken = true, label }: {
  gruppen: Gruppe[]; farben?: string[]; linieFarbe?: string; ring?: string; breite?: number; hoehe?: number;
  format?: (v: number) => string; achsenFormat?: (v: number) => string;
  /** Direktbeschriftung: letzter Linienwert bzw. erster Balken der ersten Gruppe. */
  direktLinie?: boolean; direktBalken?: boolean; label?: string;
}) {
  const n = gruppen.length;
  if (n === 0) return null;
  const padL = 50, padR = 8, padT = 24, padB = 40;
  const plotW = breite - padL - padR;
  const plotH = hoehe - padT - padB;
  const grund = padT + plotH;
  const alle = gruppen.flatMap((g) => [...g.werte, ...(g.linie != null ? [g.linie] : [])]);
  const maxWert = Math.max(0, ...alle);
  const stufe = netteStufe(maxWert / 3);
  const top = Math.max(stufe * 3, 1);
  const y = (v: number) => grund - (Math.max(0, v) / top) * plotH;
  const gruppeW = plotW / n;
  const balkenB = 24, abstand = 2;
  const k = Math.max(1, ...gruppen.map((g) => g.werte.length));
  const blockW = k * balkenB + (k - 1) * abstand;
  const cx = (i: number) => padL + gruppeW * (i + 0.5);
  const linie = gruppen
    .map((g, i) => (g.linie != null ? { x: cx(i), y: y(g.linie), wert: g.linie } : null))
    .filter((p): p is { x: number; y: number; wert: number } => p != null);
  const letzteLinie = linie[linie.length - 1];
  const ersterBalken = gruppen[0].werte[0];

  return (
    <svg viewBox={`0 0 ${breite} ${hoehe}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={label} style={{ display: 'block', width: '100%', height: 'auto', maxWidth: breite * 1.2 }}>
      {[0, 1, 2, 3].map((j) => {
        const yy = f1(y(j * stufe));
        return (
          <g key={j}>
            <line x1={padL} y1={yy} x2={breite - padR} y2={yy} stroke={T.rand} strokeWidth={1} />
            <text x={padL - 6} y={yy + 4} fontSize={10} fill={T.ged} textAnchor="end">{achsenFormat(j * stufe)}</text>
          </g>
        );
      })}
      {gruppen.map((g, i) => {
        const x0 = cx(i) - blockW / 2;
        return (
          <g key={i}>
            <title>{g.titel ?? g.label}</title>
            <rect x={f1(x0 - 6)} y={padT} width={f1(blockW + 12)} height={plotH} fill="transparent" />
            {g.werte.map((v, j) => {
              const h = v > 0 ? Math.max(1, (v / top) * plotH) : 0;
              if (h <= 0) return null;
              const x = f1(x0 + j * (balkenB + abstand));
              const fuss = Math.min(4, h);
              const farbe = farben[j] ?? T.blau;
              return (
                <g key={j}>
                  <rect x={x} y={f1(grund - h)} width={balkenB} height={f1(h)} rx={4} ry={4} fill={farbe} />
                  <rect x={x} y={f1(grund - fuss)} width={balkenB} height={f1(fuss)} fill={farbe} />
                </g>
              );
            })}
            <text x={f1(cx(i))} y={hoehe - 18} fontSize={11} fill={T.sek} textAnchor="middle">{g.label}</text>
          </g>
        );
      })}
      {linie.length > 0 ? (
        <>
          <polyline points={linie.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} fill="none" stroke={linieFarbe} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          {linie.map((p, i) => (
            <circle key={i} cx={f1(p.x)} cy={f1(p.y)} r={4} fill={linieFarbe} stroke={ring} strokeWidth={2}>
              <title>{gruppen[i]?.titel ?? format(p.wert)}</title>
            </circle>
          ))}
        </>
      ) : null}
      {direktLinie && letzteLinie ? (
        <text x={f1(Math.min(letzteLinie.x, breite - padR - 34))} y={f1(Math.max(10, letzteLinie.y - 10))} fontSize={11} fill={T.text} fontWeight={700} textAnchor="middle">{format(letzteLinie.wert)}</text>
      ) : null}
      {direktBalken && ersterBalken != null && ersterBalken > 0 ? (
        <text x={f1(cx(0) - blockW / 2 + balkenB / 2)} y={f1(Math.max(10, y(ersterBalken) - 6))} fontSize={11} fill={T.text} fontWeight={700} textAnchor="middle">{format(ersterBalken)}</text>
      ) : null}
    </svg>
  );
}

// ── Heatmap (Zellenraster) ───────────────────────────────────────────────────

export interface HeatmapZeile { label: ReactNode; werte: Array<number | null | undefined>; rechts?: ReactNode; gruppe?: string }

export function Heatmap({ zeilen, spalten, farbe, zellHoehe = 26, labelBreite = 34, spaltenBreite = 'minmax(0, 34px)', rechtsBreite, rechtsKopf, kopfGroesse = 10, titel }: {
  zeilen: HeatmapZeile[]; spalten: string[]; farbe: (v: number | null) => string; zellHoehe?: number; labelBreite?: number; spaltenBreite?: string;
  rechtsBreite?: number; rechtsKopf?: ReactNode; kopfGroesse?: number; titel?: (zeile: number, spalte: number, v: number | null) => string;
}) {
  const cols = `${labelBreite}px repeat(${Math.max(1, spalten.length)}, ${spaltenBreite})${rechtsBreite ? ` ${rechtsBreite}px` : ''}`;
  let letzteGruppe: string | undefined;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 2, alignItems: 'center', fontSize: kopfGroesse, color: T.ged }}>
      <span />
      {spalten.map((s, c) => (
        <span key={c} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{s}</span>
      ))}
      {rechtsBreite ? <span style={{ textAlign: 'right' }}>{rechtsKopf}</span> : null}
      {zeilen.map((z, r) => {
        const teile: ReactNode[] = [];
        if (z.gruppe && z.gruppe !== letzteGruppe) {
          teile.push(<span key={`g${r}`} style={{ gridColumn: '1 / -1', color: T.ged, fontWeight: 600, paddingTop: 6, fontSize: 11 }}>{z.gruppe}</span>);
        }
        letzteGruppe = z.gruppe;
        teile.push(
          <span key={`l${r}`} style={{ color: T.sek, fontWeight: 600, fontSize: Math.max(10, kopfGroesse), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={typeof z.label === 'string' ? z.label : undefined}>
            {z.label}
          </span>
        );
        spalten.forEach((_, c) => {
          const v = z.werte[c] ?? null;
          teile.push(<span key={`c${r}-${c}`} title={titel ? titel(r, c, v) : undefined} style={{ height: zellHoehe, borderRadius: 4, background: farbe(v), display: 'block' }} />);
        });
        if (rechtsBreite) teile.push(<span key={`r${r}`} style={{ textAlign: 'right', fontWeight: 700, color: T.text, fontSize: 11 }}>{z.rechts}</span>);
        return teile;
      })}
    </div>
  );
}

// ── Balkenzeile (waagerechte Balken mit Label und Wert) ──────────────────────

export interface BalkenEintrag { label: ReactNode; wert: number; farbe?: string; anzeige?: ReactNode; wertFarbe?: string; titel?: string }

export function Balkenzeile({ zeilen, max, labelBreite = 96, wertBreite = 40, hoehe = 10, farbe = T.blau, format = (v: number) => zahl(v), luecke = 8, abstand = 5, anordnung = 'neben' }: {
  zeilen: BalkenEintrag[]; max?: number; labelBreite?: number; wertBreite?: number; hoehe?: number; farbe?: string;
  format?: (v: number) => string; luecke?: number; abstand?: number;
  /** 'neben': Label | Balken | Wert. 'unter': Balken unter dem Label, Wert rechts. */
  anordnung?: 'neben' | 'unter';
}) {
  const m = max ?? Math.max(1, ...zeilen.map((z) => z.wert));
  const radius = hoehe >= 14 ? 4 : 3;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: abstand, fontSize: 12 }}>
      {zeilen.map((z, i) => {
        const pct = m > 0 && z.wert > 0 ? Math.min(100, Math.max(1, (z.wert / m) * 100)) : 0;
        const balken = <span title={z.titel} style={{ display: 'block', height: hoehe, borderRadius: `0 ${radius}px ${radius}px 0`, background: z.farbe ?? farbe, width: `${pct}%` }} />;
        const wert = <span style={{ textAlign: 'right', fontWeight: 700, color: z.wertFarbe ?? T.text, whiteSpace: 'nowrap' }}>{z.anzeige ?? format(z.wert)}</span>;
        if (anordnung === 'unter') {
          return (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: `1fr ${wertBreite}px`, gap: luecke, alignItems: 'center' }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ color: T.sek, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.label}</span>
                {balken}
              </span>
              {wert}
            </div>
          );
        }
        return (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: `${labelBreite}px 1fr ${wertBreite}px`, gap: luecke, alignItems: 'center' }}>
            <span style={{ color: T.sek, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={typeof z.label === 'string' ? z.label : undefined}>{z.label}</span>
            {balken}
            {wert}
          </div>
        );
      })}
    </div>
  );
}

// ── Segmente (geteilter Balken mit 2-px-Lücken) ──────────────────────────────

export function Segmente({ teile, hoehe = 18, radius = 6, label }: { teile: Array<{ wert: number; farbe: string; label?: string }>; hoehe?: number; radius?: number; label?: string }) {
  const ganz = teile.reduce((a, t) => a + Math.max(0, t.wert), 0);
  if (!(ganz > 0)) return <div style={{ height: hoehe, borderRadius: radius, background: T.innen }} title={label} />;
  return (
    <div role="img" aria-label={label} style={{ display: 'flex', height: hoehe, gap: 2, borderRadius: radius, overflow: 'hidden' }}>
      {teile.filter((t) => t.wert > 0).map((t, i) => (
        <span key={i} title={t.label ? `${t.label}: ${zahl(t.wert)}` : undefined} style={{ flexGrow: t.wert, flexBasis: 0, minWidth: 2, background: t.farbe }} />
      ))}
    </div>
  );
}
